import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import useFetchWithMsal from '../useFetchWithMsal';
import { protectedResources } from '../../authConfig';
import { useToast } from '../../contexts/ToastContext';

const useAccountManagerDelegations = () => {
  const { showToast } = useToast(); 
  const [editingDelegation, setEditingDelegation] = useState(null);  
  const [newDelegation, setNewDelegation] = useState({ 
    fromAccountManager: "",
    toAccountManager: "",
    startDate: "",
    endDate: "",
    notes: "",
    isActive: true
  });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(0);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [delegationToDelete, setDelegationToDelete] = useState(null);

  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });

  const queryClient = useQueryClient();

  // Fetch all delegations
  const { data: delegations, isLoading, error } = useQuery({
    queryKey: ['accountManagerDelegations', forceRefresh],
    queryFn: async () => {
      const response = await execute(
        "GET",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/account-manager-delegations`
      );
      return response;
    },
    staleTime: 30000,
  });

  // Add delegation mutation
  const addDelegationMutation = useMutation({
    mutationFn: async (delegation) => {
      const response = await execute(
        "POST",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/account-manager-delegations`,
        delegation
      );
      if (!response) throw new Error('Failed to add delegation');
      return response;
    },
    onSuccess: () => {
      showToast('ההסבה נוספה בהצלחה', "success");
      queryClient.invalidateQueries({ queryKey: ['accountManagerDelegations'] });
      setIsAddModalOpen(false);
      resetNewDelegationForm();
    },
    onError: (error) => {
      console.error('Error adding delegation:', error);
      const errorMessage = error?.data?.message || error?.msg || error?.message || 'שגיאה בהוספת הסבה';
      showToast(errorMessage, "error");
    }
  });

  // Update delegation mutation
  const updateDelegationMutation = useMutation({
    mutationFn: async (delegation) => {
      const response = await execute(
        "PUT",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/account-manager-delegations/${delegation._id}`,
        delegation
      );
      if (!response) throw new Error('Failed to update delegation');
      return response;
    },
    onSuccess: () => {
      showToast('ההסבה עודכנה בהצלחה', "success");
      queryClient.invalidateQueries({ queryKey: ['accountManagerDelegations'] });
      setIsEditModalOpen(false);
      setEditingDelegation(null);
    },
    onError: (error) => {
      console.error('Error updating delegation:', error);
      const errorMessage = error?.data?.message || error?.msg || error?.message || 'שגיאה בעדכון הסבה';
      showToast(errorMessage, "error");
    }
  });

  // Delete delegation mutation
  const deleteDelegationMutation = useMutation({
    mutationFn: async (id) => {
      const response = await execute(
        "DELETE",
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/account-manager-delegations/${id}`
      );
      if (!response) throw new Error('Failed to delete delegation');
      return response;
    },
    onSuccess: () => {
      showToast("ההסבה נמחקה בהצלחה", "success");
      queryClient.invalidateQueries({ queryKey: ['accountManagerDelegations'] });
      setIsDeleteDialogOpen(false); 
    },
    onError: (error) => {
      console.error('Error deleting delegation:', error);
      showToast('שגיאה במחיקת הסבה', "error");
    }
  });

  const refreshData = () => {
    queryClient.invalidateQueries(['accountManagerDelegations']);
    setForceRefresh(prev => prev + 1);
  };

  const resetNewDelegationForm = () => {
    setNewDelegation({ 
      fromAccountManager: "",
      toAccountManager: "",
      startDate: "",
      endDate: "",
      notes: "",
      isActive: true
    });
  };

  const handleAdd = () => {
    if (!newDelegation.fromAccountManager.trim()) {
      showToast("מנהלת חשבונות מקורית הוא שדה חובה", "error", { inline: true });
      return;
    }

    if (!newDelegation.toAccountManager.trim()) {
      showToast("מנהלת חשבונות מוסבת הוא שדה חובה", "error", { inline: true });
      return;
    }

    if (!newDelegation.startDate) {
      showToast("תאריך התחלה הוא שדה חובה", "error", { inline: true });
      return;
    }

    if (newDelegation.fromAccountManager === newDelegation.toAccountManager) {
      showToast("לא ניתן להסב ממנהלת חשבונות לעצמה", "error", { inline: true });
      return;
    }

    addDelegationMutation.mutate(newDelegation);
  };

  const handleUpdate = () => {
    if (!editingDelegation.fromAccountManager.trim()) {
      showToast("מנהלת חשבונות מקורית הוא שדה חובה", "error", { inline: true });
      return;
    }
    
    if (!editingDelegation.toAccountManager.trim()) {
      showToast("מנהלת חשבונות מוסבת הוא שדה חובה", "error", { inline: true });
      return;
    }

    if (!editingDelegation.startDate) {
      showToast("תאריך התחלה הוא שדה חובה", "error", { inline: true });
      return;
    }

    if (editingDelegation.fromAccountManager === editingDelegation.toAccountManager) {
      showToast("לא ניתן להסב ממנהלת חשבונות לעצמה", "error", { inline: true });
      return;
    }

    updateDelegationMutation.mutate(editingDelegation);
  };

  const handleDeleteClick = (id, fromAccountManager) => {
    setDelegationToDelete({ id, fromAccountManager });
    setIsDeleteDialogOpen(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setDelegationToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (delegationToDelete?.id) {
      deleteDelegationMutation.mutate(delegationToDelete.id);
    }
    setDelegationToDelete(null);
  };

  const handleEdit = (delegation) => {
    setEditingDelegation({
      ...delegation,
      startDate: delegation.startDate ? new Date(delegation.startDate).toISOString().split('T')[0] : '',
      endDate: delegation.endDate ? new Date(delegation.endDate).toISOString().split('T')[0] : ''
    });
    setIsEditModalOpen(true);
  };

  useEffect(() => {
    queryClient.invalidateQueries(['accountManagerDelegations']);
    setForceRefresh(prev => prev + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    delegations,
    isLoading,
    error,
    newDelegation,
    setNewDelegation,
    editingDelegation,
    setEditingDelegation,
    isAddModalOpen,
    setIsAddModalOpen,
    isEditModalOpen,
    setIsEditModalOpen,
    handleAdd,
    handleUpdate,
    handleDeleteClick,
    handleCancelDelete,
    handleConfirmDelete,
    isDeleteDialogOpen,
    delegationToDelete,
    isDeleting: deleteDelegationMutation.isPending,
    handleEdit,
    refreshData
  };
};

export default useAccountManagerDelegations;
